import { Box, Paper, Typography } from "@mui/material";
import type { MetricsInsightGroup, MetricsInsightTile } from "../metricsInsight";
import { KpiTile } from "./KpiTile";

interface KpiTileWallProps {
  groups: readonly MetricsInsightGroup[];
}

export function TileGroup({
  title,
  tiles,
  first,
}: {
  title: string;
  tiles: readonly MetricsInsightTile[];
  first?: boolean;
}) {
  return (
    <Box sx={{ mt: first ? 0 : 2.5 }}>
      <Typography
        component="div"
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 1 }}
      >
        {title}
      </Typography>
      {/* Uniform grid tracks — a tile is the same width whichever row it lands
          on (flex-grow stretched a lone wrapped tile across the whole row). */}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
        }}
      >
        {tiles.map((tile) => (
          <KpiTile
            key={tile.key}
            label={tile.label}
            value={tile.value}
            tooltip={tile.tooltip}
            emphasis={tile.emphasis}
          />
        ))}
      </Box>
    </Box>
  );
}

/**
 * The KPI wall — one surface holding two groups of tiles ("Contract & Health",
 * "Search & Engagement") computed from the currently-filtered rows. A typed
 * port of the POC's `renderMetricsTileWall`, including the activity-mined
 * avg-results / top-region / sample-query tiles. `metricsInsight.ts` owns the
 * calculation, emphasis, copy, formatting, grouping, and order; this module
 * only renders its projection.
 */
export function MetricsInsightGroups({ groups }: KpiTileWallProps) {
  return groups.map((group, index) => (
    <TileGroup key={group.key} title={group.title} tiles={group.tiles} first={index === 0} />
  ));
}

export function KpiTileWall({ groups }: KpiTileWallProps) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <MetricsInsightGroups groups={groups} />
    </Paper>
  );
}
